import { useAuth } from '../../../../../hooks'
import React, { FC, Fragment, useEffect, useState, useRef } from 'react'
import {
	Button,
	Dialog,
	IconButton,
	Textarea,
	Select,
	Tooltip,
} from '../../../../ui'
import {
	EditNote,
	Update,
	Delete,
	Close,
	Visibility,
} from '@mui/icons-material'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import {
	BoardStatus,
	BoardStatusCard,
	BoardUser,
	CardComment,
} from '../../../../../lib/api/types/board.interfaces'
import { useMediaQuery } from 'react-responsive'
import cn from '../../../../../lib/utils/cn'
import { PriorityLabel } from '../../../../../pages/MySpace/BoardsPages/components/ColumnCard'
import { useUserDateFormat } from '../../../../../hooks/useUserDateFormat'
import {
	useCard,
	useCreateCardComment,
	useDeleteCardComment,
	useMoveCard,
	useUpdateCard,
	useUpdateCardComment,
} from '../../../../../hooks/api/boardHooks'
import { ClassNameValue } from 'tailwind-merge'
import { CurrentUserObject } from '../../../../../lib/api/types/user.interfaces'
import CommentsList from './components/CommentsList'
import { ConfirmationModal } from '../../../common'
import FileFormatIcon from '../../../../common/FileFormatIcon'
import { shortenFileName } from '../../../../../lib/utils/formatDocument'

export interface CommentsFormValues {
	newColumnId?: string
	newAssignmentToId?: string
	content: string
	repliedComment: CardComment | null
	editingComment: CardComment | null
}

interface ViewCardModalProps {
	isOpen: boolean
	onClose: () => void
	// onEdit: (currentCardData?: BoardStatusCard | null) => void
	boardId?: string
	initialCardData?: BoardStatusCard | null
	currentColumn?: BoardStatus | null
	currentUser?: CurrentUserObject
	boardColumns?: BoardStatus[]
	boardUsers?: BoardUser[]
	isOwner?: boolean
}

const ViewCardModal: FC<ViewCardModalProps> = ({
	isOpen,
	onClose,
	// onEdit = () => {},
	boardId = '',
	initialCardData,
	currentColumn,
	currentUser,
	boardColumns = [],
	boardUsers = [],
	isOwner,
}) => {
	const { isAuthenticated } = useAuth()
	const isMobile = useMediaQuery({ maxWidth: 480 })
	const { formatDate, formatDateTime } = useUserDateFormat()

	const [cardData, setCardData] = useState(initialCardData)

	const { data: newCardData, isFetching } = useCard({
		boardId,
		cardId: initialCardData?.id ?? '',
	})

	useEffect(() => {
		setCardData(newCardData?.data || initialCardData)
	}, [initialCardData, newCardData, isFetching])

	const [deletingCommentId, setDeletingCommentId] = useState<string | null>(
		null
	)
	const commentsContainerRef = useRef<HTMLDivElement>(null)

	const { mutateAsync: createComment, isPending: isCreatingComment } =
		useCreateCardComment()
	const { mutateAsync: updateComment, isPending: isUpdatingComment } =
		useUpdateCardComment()
	const { mutateAsync: deleteComment, isPending: isDeletingComment } =
		useDeleteCardComment()
	const { mutateAsync: update, isPending: isUpdatingCard } = useUpdateCard()
	const { mutateAsync: move, isPending: isMovingCard } = useMoveCard()

	const methods = useForm<CommentsFormValues>({
		defaultValues: {
			newColumnId: initialCardData?.statusId || currentColumn?.id,
			newAssignmentToId: initialCardData?.assignedTo?.id,
			content: '',
			repliedComment: null,
			editingComment: null,
		},
	})
	const { reset, control, watch, setValue, getValues, formState } = methods
	const { isDirty, dirtyFields } = formState
	const {
		content,
		repliedComment,
		editingComment,
		newColumnId,
		newAssignmentToId,
	} = watch()

	useEffect(() => {
		if (initialCardData) {
			reset({
				newColumnId: initialCardData?.statusId || currentColumn?.id,
				newAssignmentToId: initialCardData?.assignedTo?.id,
				content: '',
				repliedComment: null,
				editingComment: null,
			})
		}
	}, [initialCardData, currentColumn?.id, reset])

	const handleClose = () => {
		onClose()
		reset({ content: '', repliedComment: null, editingComment: null })
	}

	const handleCreateComment = async () => {
		if (!content.trim() || !boardId || !initialCardData?.id || !isAuthenticated) return

		await createComment(
			{
				boardId,
				cardId: initialCardData?.id,
				data: {
					content: content?.trim(),
					parentId: repliedComment?.id || editingComment?.parentId || null,
				},
			},
			{
				onSuccess: () => {
					reset({
						...getValues(),
						content: '',
						repliedComment: null,
						editingComment: null,
					})
					setTimeout(() => {
						if (commentsContainerRef.current) {
							commentsContainerRef.current.scrollTop =
								commentsContainerRef.current.scrollHeight
						}
					}, 100)
				},
			}
		)
	}

	const handleUpdateComment = async () => {
		if (
			!content.trim() ||
			!boardId ||
			!initialCardData?.id ||
			!editingComment?.id ||
			!isAuthenticated
		)
			return

		await updateComment(
			{
				boardId,
				cardId: initialCardData?.id,
				commentId: editingComment?.id,
				data: {
					content: content?.trim(),
					parentId: editingComment?.parentId || null,
				},
			},
			{
				onSuccess: () => {
					reset({
						...getValues(),
						content: '',
						repliedComment: null,
						editingComment: null,
					})
				},
			}
		)
	}

	return (
		<Fragment>
			<Dialog
				isOpen={isOpen}
				onClose={handleClose}
				title="Task Details"
				className={cn(
					'box-border',
					isMobile ? 'w-full max-sm:w-full' : 'w-[700px] max-w-[700px]'
				)}
				controllers={
					isDirty &&
						(dirtyFields.newColumnId || dirtyFields.newAssignmentToId) ? (
						<div className="flex items-center justify-end w-full gap-2">
							<Button
								variant="tertiary"
								label={'Cancel'}
								onClick={handleClose}
							/>
							<Button
								label="Submit"
								loading={isUpdatingCard || isMovingCard}
								onClick={async () => {
									if (
										!isAuthenticated ||
										!initialCardData ||
										!newColumnId ||
										!newAssignmentToId
									)
										return

									if (newColumnId !== initialCardData?.statusId) {
										await move({
											boardId,
											cardId: initialCardData?.id,
											data: {
												position: 0,
												statusId: newColumnId,
											},
										})
									}

									if (newAssignmentToId !== initialCardData?.assignedTo?.id) {
										await update({
											data: {
												...initialCardData,
												content: initialCardData?.content ?? '',
												dueDate: String(initialCardData?.dueDate) ?? '',
												statusId: newColumnId,
												assignedToId: newAssignmentToId,
												files: [],
											},
											cardId: initialCardData?.id,
											boardId,
										})
									}

									handleClose()
								}}
							/>
						</div>
					) : null
				}
			>
				<div className="w-full flex flex-col gap-5 md:flex-row md:gap-2.5">
					<FormProvider {...methods}>
						<section
							className={cn(
								'w-full box-border flex flex-col justify-between'
								// isOwner || initialCardData?.assignedTo?.id === currentUser?.id
								// 	? 'h-[350px] md:h-[464px]'
								// 	: 'h-[350px] md:h-[435px]'
							)}
						>
							{/* Main info: title + description (content) */}
							<div
								ref={commentsContainerRef}
								className="w-full flex flex-col gap-6  pr-2.5 overflow-y-auto h-full mb-3"
							// max-h-[330px] h-fit md:h-[330px]
							>
								<h3 className="text-2xl font-medium text-text-main dark:text-text-main-dark mb-2">
									{cardData?.title}
								</h3>
								<div className="mb-5">
									<p className="mb-2 text text-text-secondary text-base dark:text-text-table-dark">
										Description
									</p>
									{cardData?.content?.trim() &&
									cardData.content.replace(/<[^>]*>/g, '').trim() ? (
										<div
											className="prose prose-sm max-w-none text-text-main dark:text-text-main-dark text-sm [&_a]:text-accent [&_a]:underline"
											dangerouslySetInnerHTML={{
												__html: cardData.content,
											}}
										/>
									) : (
										<p className="text-stroke-dark dark:text-stroke-light-dark italic text-sm">
											No details...
										</p>
									)}
								</div>

								{/* Comments List */}
								<CommentsList
									cardId={cardData?.id}
									boardId={boardId}
									isShouldFetch={isOpen}
									currentUser={currentUser}
									onDelete={setDeletingCommentId}
								/>
							</div>

							{/* Add Comment */}
							<div className="flex gap-2 pt-2">
								<UserAvatar
									className="w-8 h-8 min-w-8"
									user={{
										id: currentUser?.id || '',
										name: currentUser?.profile?.firstName || '',
										email: currentUser?.profile?.email || '',
										avatar: currentUser?.profile?.avatar || '',
									}}
								/>

								<Controller
									control={control}
									name="content"
									render={({ field: { value, onChange } }) => (
										<div className="w-full relative h-[120px] border border-stroke-dark dark:border-stroke-light-dark rounded-lg">
											<Textarea
												value={value}
												onChange={onChange}
												placeholder="Add a comment..."
												rows={2}
												wrapperClassName="flex-1"
												textareaClassName="outline-0 border-0 dark:border-0 dark:outline-0 h-auto mb-1 dark:text-stroke-light dark:placeholder:text-text-secondary"
												onEnterClick={handleCreateComment}
											/>
											<div className="w-full h-12 flex items-center justify-end pl-1 pr-3 gap-3 box-border">
												{!!repliedComment?.id && (
													<div className="flex-1 w-full flex items-start justify-start gap-0.5">
														<IconButton
															variant="transparent"
															className="w-5 h-5 p-0"
															icon={<Close className="w-4 h-4" />}
															tooltip="Remove reply"
															onClick={() => setValue('repliedComment', null)}
														/>
														<div className="max-w-[210px] border-l-2  border-stroke-dark pl-2">
															<p className="truncate text-xs text-text-secondary dark:text-text-table-dark">
																Replied to:{' '}
																{repliedComment?.createdBy?.name ||
																	repliedComment?.createdBy?.email}
															</p>
															<p className="truncate text-sm text-text-main dark:text-text-main-dark">
																{repliedComment?.content}
															</p>
														</div>
													</div>
												)}
												{editingComment?.id ? (
													<div className="flex items-center justify-end gap-2">
														<Button
															variant="tertiary"
															label="Cancel"
															onClick={() =>
																reset({
																	...getValues(),
																	content: '',
																	repliedComment: null,
																	editingComment: null,
																})
															}
															tooltip="Cancel"
														/>
														<Button
															variant="primary"
															label="Save"
															onClick={handleUpdateComment}
															loading={isUpdatingComment}
															disabled={!value?.trim()}
														/>
													</div>
												) : (
													<Button
														variant="primary"
														label="Send"
														onClick={handleCreateComment}
														loading={isCreatingComment}
														disabled={!value?.trim()}
													/>
												)}
											</div>
										</div>
									)}
								/>
							</div>
						</section>
					</FormProvider>

					<section
						className={cn(
							'flex flex-col rounded-lg h-full border border-stroke-dark dark:border-stroke-light-dark box-border md:ml-2.5',
							isMobile ? 'w-full' : 'w-[252px] min-w-[252px]'
						)}
					>
						<div className="flex flex-col p-3 gap-5">
							<div className="flex flex-col gap-2 w-full">
								<span className="text-text-secondary text-base  dark:text-text-table-dark">
									Created by
								</span>
								<div className="flex items-center justify-start gap-2">
									<UserAvatar user={cardData?.createdBy} />
									<span className="text-text-main dark:text-text-main-dark font-medium text-sm">
										{cardData?.createdBy?.name}
									</span>
								</div>
							</div>
							<div className="flex flex-col gap-2 w-full">
								<span className="text-text-secondary text-base dark:text-text-table-dark">
									Status
								</span>
								{isOwner ||
									initialCardData?.assignedTo?.id === currentUser?.id ? (
									<Controller
										control={control}
										name="newColumnId"
										render={({ field: { value, onChange } }) => (
											<Select
												options={boardColumns?.map((c) => ({
													value: c?.id,
													label: c?.name,
												}))}
												value={value}
												onChange={onChange}
												placeholder="Select status"
												wrapperClassName="w-full"
												className="w-full h-9"
											/>
										)}
									/>
								) : (
									<div className="flex items-center justify-start gap-2">
										<div
											className="h-3.5 w-0.5 rounded-full"
											style={{
												backgroundColor: currentColumn?.color ?? '#FF73A6',
											}}
										/>
										<span className="text-text-secondary dark:text-text-table-dark text-sm">
											{currentColumn?.name}
										</span>
									</div>
								)}
							</div>

							<div className="flex items-center gap-2">
								<div className="flex flex-col gap-2 w-full">
									<span className="text-text-secondary text-base dark:text-text-table-dark">
										Priority
									</span>
									{cardData?.priority ? (
										<PriorityLabel
											priority={cardData?.priority}
											className="h-3 px-1.5 text-[10px]"
										/>
									) : (
										<span className="text-xs italic text-text-secondary">
											No priority
										</span>
									)}
								</div>
								<div className="flex flex-col gap-2 w-full">
									<span className="text-text-secondary text-base dark:text-text-table-dark">
										Due date
									</span>
									<span className="text-text-main dark:text-text-main-dark font-medium text-sm">
										{cardData?.dueDate
											? formatDate(cardData.dueDate)
											: '-'}
									</span>
								</div>
							</div>

							<div className="flex flex-col gap-2 w-full">
								<span className="text-text-secondary text-base dark:text-text-table-dark">
									Assignment
								</span>
								{isOwner ? (
									<Controller
										control={control}
										name="newAssignmentToId"
										render={({ field: { value, onChange } }) => (
											<Select
												options={[
													{ value: '', label: 'No assignment', isEmpty: true },
													...boardUsers.map((u) => ({
														value: u?.id,
														label: u?.name || u?.email,
													})),
												]}
												value={value}
												onChange={onChange}
												placeholder="Select user"
												wrapperClassName="w-full"
												selectClassName="w-full h-9"
												className="w-full"
											/>
										)}
									/>
								) : (
									<div className="flex items-center justify-start gap-2 dark:text-text-table-dark">
										{!cardData?.assignedTo ? (
											<span className="text-text-secondary text-sm">
												Unassigned
											</span>
										) : (
											''
										)}
										<UserAvatar user={cardData?.assignedTo} />
										<span className="text-text-main font-medium text-sm dark:text-text-main-dark">
											{cardData?.assignedTo?.name}
										</span>
									</div>
								)}
							</div>

							{/* {!!cardData?.files?.length && ( */}
							<div>
								<p className="text-text-secondary text-base dark:text-text-table-dark mb-2">
									Linked files
								</p>
								<div className={cn("max-h-[100px] overflow-y-auto border text-text-secondary dark:text-text-table-dark hover:text-text-main dark:hover:text-text-main-dark duration-default rounded border-stroke-light dark:border-stroke-light-dark", !cardData?.files?.length && "border-0 bg-background-light dark:bg-background-light-dark p-2")}>
									{cardData?.files?.length ? cardData?.files?.map(({ id, name }) => (
										<div
											key={id}
											className="px-2 py-1 flex items-center justify-start gap-2 border-b border-b-stroke-light dark:border-b-stroke-light-dark last:border-0 hover:bg-background-light-hover dark:hover:bg-background-light-hover-dark group"
										>
											<FileFormatIcon fileName={name} size={20} />
											<Tooltip
												label={name}
												className={cn(isMobile && 'opacity-0')}
											>
												<p className="text-text-main  dark:text-text-table-dark text-sm">
													{shortenFileName(name, 10)}
												</p>
											</Tooltip>

											<IconButton
												icon={<Visibility className="w-4 h-4" />}
												variant="secondary"
												className="w-7 h-7 ml-auto opacity-0 group-hover:opacity-100"
												tooltip="Preview"
												onClick={() => {
													window.open(
														`/aura/preview?is_blank=true&id=${id}`,
														'_blank'
													)
												}}
											/>
										</div>
									)) : <p className="text-text-secondary italic text-sm text-center">
										No files
									</p>}
								</div>
							</div>
						</div>
						<div className="p-3 flex flex-col gap-1 border-t border-stroke-dark dark:border-stroke-light-dark">
							<p className="flex items-center justify-start gap-1 text-text-secondary  dark:text-text-table-dark text-xs">
								<EditNote className="w-5 h-5 fill-text-secondary dark:text-text-table-dark" />{' '}
								Created{' '}
								{cardData?.createdAt
									? formatDateTime(cardData.createdAt)
									: '-'}
							</p>
							<p className="flex items-center justify-start gap-1 text-text-secondary dark:text-text-table-dark text-xs">
								<Update className="w-5 h-5 fill-text-secondary dark:text-text-table-dark" />{' '}
								Updated{' '}
								{cardData?.updatedAt
									? formatDateTime(cardData.updatedAt)
									: '-'}
							</p>
						</div>
					</section>
				</div>
			</Dialog>

			<ConfirmationModal
				isOpen={!!deletingCommentId}
				onClose={() => setDeletingCommentId(null)}
				loading={isDeletingComment}
				onAction={async () => {
					if (!deletingCommentId || !boardId || !cardData?.id) return
					await deleteComment(
						{
							boardId,
							cardId: cardData?.id,
							commentId: deletingCommentId,
						},
						{
							onSuccess: () => setDeletingCommentId(null),
						}
					)
				}}
				title="Delete comment"
				icon={<Delete />}
				description={
					"The comment will be deleted, but replies will remain. Deleted comments can't be recovered."
				}
				overlayClassName="bg-black/30"
			/>
		</Fragment>
	)
}

export default ViewCardModal

export const UserAvatar = ({
	user,
	className,
}: {
	user?: BoardUser
	className?: ClassNameValue
}) => {
	return (
		<div
			className={cn(
				'w-6 h-6 overflow-hidden rounded-lg flex items-center justify-center',
				className
			)}
		>
			{user?.avatar ? (
				<img
					className="w-full h-full rounded-lg  box-border object-cover"
					src={user?.avatar}
				/>
			) : (
				<span className="text-text-secondary text-xs">
					{user?.name?.[0] ?? 'U'}
				</span>
			)}
		</div>
	)
}
